import React, { useState } from 'react';
import { ExternalSystemId, ModelGroupDTO, useModelGroupsQuery } from './api/useModelGroupsQuery';
import { ErrorState } from '../../../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { Box } from '../../../CommonComponents/OPL/Box/Box';
import { Text } from '../../../CommonComponents/OPL/Text/Text';
import { PageWrapper } from '../../../CommonComponents/PageWrapper/PageWrapper';
import { Button } from '../../../CommonComponents/OPL/Button/Button';
import { UpsertModelGroup } from './components/UpsertModelGroup/UpsertModelGroup';
import { ModelGroups } from './components/ModelGroups/ModelGroups';
import { SelectModelGroupType } from './components/SelectModelGroupType/SelectModelGroupType';
import { ModelGroupType } from './Models.constants';
import { useIsOrionAdviser } from './hooks/useIsOrionAdviser';
import { useSyncModelsMutation } from './api/useSyncModelsMutation';
import { MESSAGES } from '../../../CommonConstants';
import { toast } from '../../../CommonComponents/OPL/Toast/Toast';
import { Alert } from '../../../CommonComponents/OPL/Alert/Alert';
import { useRiskalyzeModelGroup } from './hooks/useRiskalyzeModelGroup';

export const Models = () => {
  const [isSelectModelGroupTypeOpen, setIsSelectModelGroupTypeOpen] = useState(false);

  const [modelGroupToEdit, setModelGroupToEdit] = useState<Partial<ModelGroupDTO> | null>(null);

  const { isFetching, isError, refetch, data } = useModelGroupsQuery();

  const syncModelsMutation = useSyncModelsMutation();

  const isOrionAdviser = useIsOrionAdviser();

  const riskalyzeModelGroup = useRiskalyzeModelGroup();

  if (isError) {
    return (
      <Box mt="56">
        <ErrorState onAction={refetch} />
      </Box>
    );
  }

  const modelGroupTypeSelectionOptions = [
    {
      value: ModelGroupType.Orion,
      label: 'Import Models',
      action: () => setModelGroupToEdit({ externalSystem: ExternalSystemId.Orion }),
    },
    {
      value: ModelGroupType.Custom,
      label: 'Create Custom Models',
      action: () => setModelGroupToEdit({}),
    },
  ];

  const handleAddGroup = () => {
    if (isOrionAdviser) {
      setIsSelectModelGroupTypeOpen(true);
    } else {
      setModelGroupToEdit({});
    }
  };

  const handleSyncModels = async () => {
    try {
      await syncModelsMutation.mutateAsync();

      refetch();
    } catch (error) {
      console.error(error);
      toast.error(MESSAGES.GENERIC_ERROR);
    }
  };

  return (
    <>
      <PageWrapper>
        <Box display="flex" mt="24" css="gap: 16px">
          <Box flex={1}>
            <Box display="flex" flexDirection="column" maxWidth={800} css="gap: 12px">
              <Text>
                Model portfolios are used in the Risk Assessment and Asset Allocation Calibration
                pages. To ensure proper mapping of client risk profile to appropriate models, list
                models from most Conservative to most Aggressive.
              </Text>

              {riskalyzeModelGroup && (
                <Alert
                  variant="info"
                  message="Asset Allocations from Riskalyze have successfully been imported. If you'd like to change the Asset Allocations listed below, you can do so via Riskalyze's website."
                  extraAction={{
                    text: 'Change Here',
                    action() {
                      window.open('https://www.riskalyze.com/', '_blank');
                    },
                  }}
                />
              )}
            </Box>
          </Box>

          <Box display="flex" flexDirection="column" css="gap: 12px">
            <Button onClick={handleAddGroup} disabled={isFetching}>
              Create Model Group
            </Button>

            {data?.options.hasOrionModels && (
              <Button
                variant="secondary"
                isLoading={syncModelsMutation.isLoading}
                onClick={handleSyncModels}
                disabled={isFetching}
              >
                Sync New Asset Classes from Orion
              </Button>
            )}
          </Box>
        </Box>
      </PageWrapper>

      <ModelGroups setModelGroupToEdit={setModelGroupToEdit} />

      {isSelectModelGroupTypeOpen && (
        <SelectModelGroupType
          options={modelGroupTypeSelectionOptions}
          onClose={() => setIsSelectModelGroupTypeOpen(false)}
        />
      )}

      {modelGroupToEdit && (
        <UpsertModelGroup modelGroup={modelGroupToEdit} onClose={() => setModelGroupToEdit(null)} />
      )}
    </>
  );
};
